import React, { useState, useEffect, startTransition, useDeferredValue } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import FolderIcon from '@mui/icons-material/Folder';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { uploadMedia, getMedias, deleteMedia, getMediaLinks, linkMedia, unlinkMedia, updateMediaOrder, updateMediaMetadata } from '../app/api';
import { useTranslation } from '../app/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
export const PRODUCT_DEFAULT_IMAGE = '/images/product-default.svg';

function getFileIcon(mimeType, filename) {
  if (mimeType?.startsWith('image/')) {
    return <ImageIcon sx={{ fontSize: 60, color: 'primary.main' }} />;
  }
  if (mimeType === 'application/pdf' || filename?.toLowerCase().endsWith('.pdf')) {
    return <PictureAsPdfIcon sx={{ fontSize: 60, color: 'error.main' }} />;
  }
  if (mimeType?.startsWith('video/')) {
    return <VideoFileIcon sx={{ fontSize: 60, color: 'secondary.main' }} />;
  }
  if (mimeType?.includes('word') || mimeType?.includes('document') || filename?.match(/\.(doc|docx)$/i)) {
    return <DescriptionIcon sx={{ fontSize: 60, color: 'info.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: 60, color: 'text.secondary' }} />;
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function SortableMediaItem({ media, onPreview, onDelete, onEditTitle, readOnly, metadataKey, metadataValue }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(media.metadataJSON?.title || media.filename);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.id });

  useEffect(() => {
    setTitleValue(media.metadataJSON?.title || media.filename);
  }, [media.metadataJSON?.title, media.filename]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTitleSave = () => {
    if (onEditTitle && media.metadataJSON) {
      onEditTitle(media.id, titleValue);
    }
    setEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setTitleValue(media.metadataJSON?.title || media.filename);
    setEditingTitle(false);
  };

  const isImage = media.mime_type?.startsWith('image/');
  const fileIcon = getFileIcon(media.mime_type, media.filename);

  return (
    <Grid item xs={6} sm={4} md={3} lg={2} ref={setNodeRef} style={style}>
      <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!readOnly && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 2,
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
            {...attributes}
            {...listeners}
          >
            <IconButton size="small" sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', boxShadow: 1 }}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {isImage ? (
          <CardMedia
            component="img"
            height="150"
            image={`${API_URL}${media.file_path}`}
            alt={media.filename}
            sx={{ objectFit: 'cover', cursor: 'pointer' }}
            onClick={() => onPreview(media)}
          />
        ) : (
          <Box
            sx={{
              height: 150,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'grey.200',
              },
            }}
            onClick={() => onPreview(media)}
          >
            {fileIcon}
          </Box>
        )}
        <Box sx={{ p: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {editingTitle && !readOnly ? (
            <TextField
              fullWidth
              size="small"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTitleSave();
                } else if (e.key === 'Escape') {
                  handleTitleCancel();
                }
              }}
              autoFocus
              sx={{ mb: 1 }}
            />
          ) : (
            <>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  cursor: readOnly ? 'default' : 'pointer',
                  fontWeight: 'medium',
                  mb: 0.5,
                  '&:hover': readOnly ? {} : { textDecoration: 'underline' },
                }}
                onClick={() => !readOnly && setEditingTitle(true)}
                title={readOnly ? '' : 'Clique para editar'}
                noWrap
              >
                {media.metadataJSON?.title || media.filename}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.7rem' }}>
                {formatFileSize(media.file_size || 0)}
              </Typography>
            </>
          )}
        </Box>
        <CardActions sx={{ justifyContent: 'center', p: 1, pt: 0 }}>
          {!readOnly && (
            <>
              <IconButton size="small" onClick={() => onPreview(media)} title="Visualizar">
                <VisibilityIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => !editingTitle && setEditingTitle(true)}
                title="Editar título"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(media)}
                title="Remover"
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
          {readOnly && (
            <IconButton size="small" onClick={() => onPreview(media)} title="Visualizar">
              <VisibilityIcon />
            </IconButton>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
}

const MediaGallery = ({
  metadataKey,
  metadataValue,
  accept = 'image/*',
  maxFiles,
  onMediaChange,
  readOnly = false,
  refreshKey,
}) => {
  const { translate } = useTranslation();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const deferredUploadProgress = useDeferredValue(uploadProgress);
  const [previewDialog, setPreviewDialog] = useState({ open: false, media: null });
  const [selectDialog, setSelectDialog] = useState({ open: false, selectedMedias: [] });
  const [availableMedias, setAvailableMedias] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadMedias = async () => {
    if (!metadataValue) {
      setMedias([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getMedias();
      const allMedias = response.data.medias || [];
      
      const filteredMedias = allMedias
        .map((media) => {
          if (!media.metadata || media.metadata.length === 0) {
            return null;
          }
          
          const matchingMeta = media.metadata.find((meta) => {
            if (meta.key !== metadataKey) return false;
            const metaValue = meta.value;
            const isObject = typeof metaValue === 'object' && metaValue !== null;
            if (isObject) {
              return metaValue.id === metadataValue;
            }
            return metaValue === metadataValue;
          });
          
          if (!matchingMeta) {
            return null;
          }
          
          const metaValue = matchingMeta.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          
          if (!isObject) {
            return {
              ...media,
              metadataJSON: { id: metadataValue, url: '', title: media.filename, order: 0 },
              order: 0,
            };
          }
          
          return {
            ...media,
            metadataJSON: metaValue,
            order: metaValue?.order || 0,
          };
        })
        .filter((media) => media !== null)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      startTransition(() => {
        setMedias(filteredMedias);
        if (onMediaChange) {
          onMediaChange(filteredMedias);
        }
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataKey, metadataValue, refreshKey]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) {
      return;
    }

    if (maxFiles && medias.length + files.length > maxFiles) {
      setSnackbar({
        open: true,
        message: `Máximo de ${maxFiles} arquivos permitidos`,
        severity: 'error',
      });
      return;
    }

    event.target.value = '';

    const progressMap = {};
    files.forEach((file) => {
      progressMap[file.name] = 0;
    });
    
    startTransition(() => {
      setUploadProgress(progressMap);
    });
    
    const startUpload = () => {
      setUploading(true);
      
      const uploadPromises = files.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata_key', metadataKey);
        
        let url = '';
        if (metadataKey === 'product_image' && metadataValue) {
          url = `/products/${metadataValue}`;
        }
        
        const metadataJSON = {
          id: metadataValue,
          url: url,
          title: file.name,
          order: medias.length + index,
        };
        formData.append('metadata_json', JSON.stringify(metadataJSON));
        
        startTransition(() => {
          setUploadProgress((prev) => ({ ...prev, [file.name]: 20 }));
        });
        
        try {
          const result = await uploadMedia(formData);
          startTransition(() => {
            setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          });
          return result;
        } catch (err) {
          startTransition(() => {
            setUploadProgress((prev) => ({ ...prev, [file.name]: -1 }));
          });
          throw err;
        }
      });

      Promise.all(uploadPromises)
        .then(() => {
          startTransition(() => {
            setSnackbar({
              open: true,
              message: translate('media.uploaded'),
              severity: 'success',
            });
            setUploadProgress({});
            setUploading(false);
          });
          setTimeout(() => {
            startTransition(() => {
              loadMedias();
            });
          }, 500);
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: error.message,
            severity: 'error',
          });
          startTransition(() => {
            setUploadProgress({});
            setUploading(false);
          });
        });
    };
    
    if (window.requestIdleCallback) {
      window.requestIdleCallback(startUpload, { timeout: 0 });
    } else {
      setTimeout(startUpload, 0);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (readOnly) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const fakeEvent = {
        target: {
          files: e.dataTransfer.files,
          value: '',
        },
      };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = medias.findIndex((media) => media.id === active.id);
    const newIndex = medias.findIndex((media) => media.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newMedias = arrayMove(medias, oldIndex, newIndex);
    setMedias(newMedias);

    try {
      const mediaOrders = newMedias.map((media, index) => ({
        media_id: media.id,
        order: index,
      }));

      await updateMediaOrder(metadataKey, metadataValue, mediaOrders);
      
      setSnackbar({
        open: true,
        message: translate('media.order_updated'),
        severity: 'success',
      });
      
      if (onMediaChange) {
        onMediaChange(newMedias);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao atualizar ordem',
        severity: 'error',
      });
      await loadMedias();
    }
  };

  const handlePreview = (media) => {
    setPreviewDialog({ open: true, media });
  };

  const handleEditTitle = async (mediaId, newTitle) => {
    try {
      const media = medias.find((m) => m.id === mediaId);
      if (!media || !media.metadataJSON) {
        return;
      }

      // Se for product_image e não tiver URL, adiciona automaticamente
      let url = media.metadataJSON.url || '';
      if (metadataKey === 'product_image' && !url && metadataValue) {
        url = `/products/${metadataValue}`;
      }

      await updateMediaMetadata(
        mediaId,
        metadataKey,
        media.metadataJSON.id,
        newTitle,
        url
      );

      setSnackbar({
        open: true,
        message: translate('media.title_updated'),
        severity: 'success',
      });

      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao atualizar título',
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = async (media) => {
    try {
      const response = await getMediaLinks(media.id);
      const links = response.data.links || [];
      
      if (links.length === 1) {
        await deleteMedia(media.id);
        setSnackbar({
          open: true,
          message: translate('media.deleted'),
          severity: 'success',
        });
      } else {
        const matchingMeta = media.metadata?.find((m) => {
          if (m.key !== metadataKey) return false;
          const metaValue = m.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          if (isObject) {
            return metaValue.id === metadataValue;
          }
          return metaValue === metadataValue;
        });
        if (matchingMeta) {
          const metaValue = matchingMeta.value;
          const valueToSend = typeof metaValue === 'object' && metaValue !== null
            ? JSON.stringify(metaValue)
            : metaValue;
          await unlinkMedia(media.id, metadataKey, valueToSend);
        }
        setSnackbar({
          open: true,
          message: translate('media.unlinked'),
          severity: 'success',
        });
      }
      
      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleSelectExistingClick = async () => {
    try {
      setLoadingAvailable(true);
      const response = await getMedias();
      const allMedias = response.data.medias || [];
      
      const availableMedias = allMedias.filter((media) => {
        if (!media.metadata || media.metadata.length === 0) {
          return true;
        }
        
        const alreadyLinked = media.metadata.some((meta) => {
          if (meta.key !== metadataKey) return false;
          const metaValue = meta.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          if (isObject) {
            return metaValue.id === metadataValue;
          }
          return metaValue === metadataValue;
        });
        
        return !alreadyLinked;
      });

      setAvailableMedias(availableMedias);
      setSelectDialog({ open: true, selectedMedias: [] });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleToggleMediaSelection = (mediaId) => {
    setSelectDialog((prev) => {
      const isSelected = prev.selectedMedias.includes(mediaId);
      return {
        ...prev,
        selectedMedias: isSelected
          ? prev.selectedMedias.filter((id) => id !== mediaId)
          : [...prev.selectedMedias, mediaId],
      };
    });
  };

  const handleLinkSelectedMedias = async () => {
    const { selectedMedias } = selectDialog;
    if (selectedMedias.length === 0) {
      setSnackbar({
        open: true,
        message: translate('media.select_at_least_one'),
        severity: 'error',
      });
      return;
    }

    try {
      setUploading(true);
      const linkPromises = selectedMedias.map(async (mediaId, index) => {
        // Se for product_image, inclui a URL de visualização do produto
        let url = '';
        if (metadataKey === 'product_image' && metadataValue) {
          url = `/products/${metadataValue}`;
        }
        
        const metadataJSON = {
          id: metadataValue,
          url: url,
          title: '',
          order: medias.length + index,
        };
        
        return linkMedia(mediaId, {
          [metadataKey]: JSON.stringify(metadataJSON),
        });
      });

      await Promise.all(linkPromises);
      setSnackbar({
        open: true,
        message: translate('media.linked_success'),
        severity: 'success',
      });
      setSelectDialog({ open: false, selectedMedias: [] });
      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSelectCancel = () => {
    setSelectDialog({ open: false, selectedMedias: [] });
  };

  if (!metadataValue) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {translate('media.save_first')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box
            sx={{
              flex: 1,
              p: 2,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              accept={accept}
              style={{ display: 'none' }}
              id={`media-gallery-upload-${metadataKey}-${metadataValue}`}
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={(maxFiles && medias.length >= maxFiles)}
              key={`upload-${metadataKey}-${metadataValue}`}
            />
            <label htmlFor={`media-gallery-upload-${metadataKey}-${metadataValue}`}>
              <Button
                variant="outlined"
                component="span"
                startIcon={uploading ? <CircularProgress size={16} /> : <AddIcon />}
                disabled={(maxFiles && medias.length >= maxFiles)}
                sx={{ mb: 1 }}
                fullWidth
              >
                {uploading ? translate('common.uploading') : translate('media.upload_images')}
              </Button>
            </label>
            {uploading && Object.keys(deferredUploadProgress).length > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                {Object.entries(deferredUploadProgress).map(([fileName, progress]) => (
                  <Box key={fileName} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1, fontSize: '0.7rem' }}>
                        {fileName}
                      </Typography>
                      {progress === 100 && <Chip label="Concluído" size="small" color="success" sx={{ height: 20 }} />}
                      {progress === -1 && <Chip label="Erro" size="small" color="error" sx={{ height: 20 }} />}
                      {progress > 0 && progress < 100 && (
                        <Chip label={`${progress}%`} size="small" sx={{ height: 20 }} />
                      )}
                    </Box>
                    {progress > 0 && progress < 100 && (
                      <LinearProgress variant="determinate" value={progress} />
                    )}
                    {progress === 100 && (
                      <LinearProgress variant="determinate" value={100} color="success" />
                    )}
                    {progress === -1 && (
                      <LinearProgress variant="determinate" value={100} color="error" />
                    )}
                  </Box>
                ))}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('media.drag_drop')} {maxFiles && `(${medias.length}/${maxFiles})`}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 2,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={handleSelectExistingClick}
              fullWidth
              sx={{ mb: 1 }}
            >
              {translate('media.select_existing')}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('media.select_from_library')}
            </Typography>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : medias.length === 0 ? (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ position: 'relative', height: '100%' }}>
              <CardMedia
                component="img"
                height="150"
                image={PRODUCT_DEFAULT_IMAGE}
                alt={translate('media.no_images')}
                sx={{ objectFit: 'cover' }}
              />
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" display="block" textAlign="center" color="text.secondary">
                  {translate('media.no_images')}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={medias.map((m) => m.id)}
            strategy={rectSortingStrategy}
          >
            <Grid container spacing={2}>
              {medias.map((media) => (
                <SortableMediaItem
                  key={media.id}
                  media={media}
                  onPreview={handlePreview}
                  onDelete={handleDeleteClick}
                  onEditTitle={handleEditTitle}
                  readOnly={readOnly}
                  metadataKey={metadataKey}
                  metadataValue={metadataValue}
                />
              ))}
            </Grid>
          </SortableContext>
        </DndContext>
      )}

      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, media: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {previewDialog.media && getFileIcon(previewDialog.media.mime_type, previewDialog.media.filename)}
            <Box>
              <Typography variant="h6">{previewDialog.media?.filename}</Typography>
              {previewDialog.media && (
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(previewDialog.media.file_size || 0)} • {previewDialog.media.mime_type || 'Tipo desconhecido'}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewDialog.media && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              {previewDialog.media.mime_type?.startsWith('image/') ? (
                <img
                  src={`${API_URL}${previewDialog.media.file_path}`}
                  alt={previewDialog.media.filename}
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '70vh' }}
                />
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    {getFileIcon(previewDialog.media.mime_type, previewDialog.media.filename)}
                  </Box>
                  <Button
                    variant="contained"
                    component="a"
                    href={`${API_URL}${previewDialog.media.file_path}`}
                    download={previewDialog.media.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Baixar Arquivo
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, media: null })}>
            {translate('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selectDialog.open}
        onClose={handleSelectCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{translate('media.select_existing_images')}</DialogTitle>
        <DialogContent>
          {loadingAvailable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableMedias.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translate('media.no_available_images')}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {availableMedias.map((media) => (
                <Grid item xs={6} sm={4} md={3} key={media.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectDialog.selectedMedias.includes(media.id)
                        ? '2px solid'
                        : '1px solid',
                      borderColor: selectDialog.selectedMedias.includes(media.id)
                        ? 'primary.main'
                        : 'divider',
                    }}
                    onClick={() => handleToggleMediaSelection(media.id)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      {media.mime_type?.startsWith('image/') ? (
                        <CardMedia
                          component="img"
                          height="120"
                          image={`${API_URL}${media.file_path}`}
                          alt={media.filename}
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                          }}
                        >
                          {getFileIcon(media.mime_type, media.filename)}
                        </Box>
                      )}
                      <Checkbox
                        checked={selectDialog.selectedMedias.includes(media.id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleMediaSelection(media.id);
                        }}
                      />
                    </Box>
                    <CardActions sx={{ p: 1, justifyContent: 'center', flexDirection: 'column' }}>
                      <Typography variant="caption" noWrap sx={{ width: '100%', textAlign: 'center', fontWeight: 'medium' }}>
                        {media.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {formatFileSize(media.file_size || 0)}
                      </Typography>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSelectCancel}>
            {translate('common.cancel')}
          </Button>
          <Button
            onClick={handleLinkSelectedMedias}
            variant="contained"
            disabled={selectDialog.selectedMedias.length === 0 || uploading}
          >
            {uploading ? translate('common.loading') : translate('common.add')} ({selectDialog.selectedMedias.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaGallery;
